import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import {
  KNOWN_PLACEHOLDERS,
  PLACEHOLDERS_WITHOUT_SOURCE,
  PLACEHOLDER_LABELS,
  PROMPT_STATES,
  type PromptPlaceholder,
} from '@seo/contracts';
import { CreatePanel } from '../components/CreatePanel';
import { DataTable, type Column } from '../components/DataTable';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import { diffSummary, lineDiff } from '../lib/line-diff';
import { zodResolver } from '../lib/zod-resolver';
import classes from './EntityPage.module.css';

/**
 * Промпт-профілі (§3) — другий екран, якого не існувало.
 *
 * `/prompts` віддавав заглушку, а шість процедур неймспейсу не мали викликача. Профіль
 * версіонується, а не редагується на місці: блок шаблону вказує на конкретну версію, тож
 * правка промпту сьогодні не може мовчки змінити те, з чого згенерована сторінка минулого
 * тижня. Тому «Зберегти» тут — це **нова версія**, і кнопка каже саме це.
 *
 * У мокапі (`promptEdit`, рядки 2465–2522) кнопки збереження немає взагалі, а палітра змінних
 * — шістнадцять рядків, з яких девʼять контекст генерації не вміє підставити. Палітра тут
 * показує те, що справді підставляється, і окремо позначає те, під що джерела немає:
 * невідомий плейсхолдер сервер відхилить, а відомий-без-джерела доїде в модель дужками.
 */

interface PromptRow {
  id: string;
  name: string;
  version: number;
  state: string;
  pageType: string | null;
  model: string;
  usedByBlocks: number;
}

const createSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву профілю').max(200),
  model: z.string().trim().min(1, 'Вкажіть модель').max(120),
  promptBody: z
    .string()
    .trim()
    .min(1, 'Тіло промпту порожнє')
    .max(20_000)
    .refine((body) => body.includes('{brand}') && body.includes('{geo}'), {
      message: 'Промпт без {brand} і {geo} згенерує однаковий текст для всієї сітки',
    }),
});
type CreateValues = z.infer<typeof createSchema>;

const STATE_LABELS: Record<string, string> = {
  live: 'LIVE',
  draft: 'чернетка',
  fixed: 'фікс',
};
const STATE_COLORS: Record<string, string> = { live: 'mint', draft: 'amber', fixed: 'slate' };

const withoutSource = new Set<string>(PLACEHOLDERS_WITHOUT_SOURCE);

/** `{placeholder}` у тілі — той самий розбір, що на сервері. */
function placeholdersIn(body: string): string[] {
  return [
    ...new Set([...body.matchAll(/\{([a-z_][a-z0-9_]*)\}/giu)].map((match) => match[1] ?? '')),
  ].filter((name) => name.length > 0);
}

export function PromptsPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [state, setState] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  /** `null` — «оператор не чіпав тіло»; показуємо збережене. */
  const [bodyDraft, setBodyDraft] = useState<string | null>(null);
  const [testContext, setTestContext] = useState<Record<string, string>>({});
  const canWrite = useCan('ai_generation', 'write');

  const list = useQuery(
    trpc.prompts.list.queryOptions({
      ...(search ? { search } : {}),
      ...(state === null ? {} : { states: [state as (typeof PROMPT_STATES)[number]] }),
      pagination: { limit: 100, direction: 'asc' },
    }),
  );

  const detail = useQuery({
    ...trpc.prompts.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.prompts.pathKey() });
  };

  const createForm = useForm<CreateValues>({
    initialValues: { name: '', model: 'anthropic/claude-sonnet-5', promptBody: '' },
    validate: zodResolver(createSchema),
  });

  const create = useMutation(
    trpc.prompts.create.mutationOptions({
      onSuccess: async (result) => {
        createForm.reset();
        setCreateOpen(false);
        setSelectedId(result.id);
        setBodyDraft(null);
        await invalidate();
      },
    }),
  );

  /**
   * Ревізія віддає **id нової версії** — на неї й перемикаємось. Інакше екран показував би
   * попередню версію як поточну, а наступне збереження ревізувало б уже застарілий рядок.
   */
  const revise = useMutation(
    trpc.prompts.revise.mutationOptions({
      onSuccess: async (result) => {
        setSelectedId(result.id);
        setBodyDraft(null);
        await invalidate();
      },
    }),
  );
  const publish = useMutation(trpc.prompts.publish.mutationOptions({ onSuccess: invalidate }));
  const testRun = useMutation(trpc.prompts.test.mutationOptions());

  const columns: Column<PromptRow>[] = [
    { key: 'name', header: 'Профіль', render: (row) => <span>{row.name}</span> },
    {
      key: 'version',
      header: 'Верс.',
      align: 'right',
      render: (row) => <span className={classes.mono}>v{row.version}</span>,
    },
    {
      key: 'state',
      header: 'Стан',
      render: (row) => (
        <Badge size="sm" color={STATE_COLORS[row.state] ?? 'slate'} data-testid={`prompt-state-${row.name}`}>
          {STATE_LABELS[row.state] ?? row.state}
        </Badge>
      ),
    },
    {
      key: 'pageType',
      header: 'Тип сторінки',
      render: (row) => <span className={classes.dim}>{row.pageType ?? '—'}</span>,
    },
    { key: 'model', header: 'Модель', render: (row) => <span className={classes.mono}>{row.model}</span> },
    {
      key: 'blocks',
      header: 'Блоків',
      align: 'right',
      /** Радіус вибуху ревізії: скільки блоків шаблонів указують на цю версію. */
      render: (row) => (
        <span className={classes.mono} data-testid={`prompt-blocks-${row.name}`}>
          {row.usedByBlocks}
        </span>
      ),
    },
  ];

  const profile = detail.data;
  const body = bodyDraft ?? profile?.promptBody ?? '';
  /**
   * Попередня версія — наступний рядок історії: `versions` віддаються за спаданням номера.
   * Порівнюємо збережене тіло цієї версії, а не чернетку в полі: діфф про те, що вже
   * сталося, а не про те, що оператор друкує зараз.
   */
  const versionIndex = profile?.versions.findIndex((entry) => entry.id === profile.id) ?? -1;
  const previousVersion =
    versionIndex >= 0 ? profile?.versions[versionIndex + 1] : undefined;
  const diffLines =
    previousVersion === undefined || profile === undefined
      ? []
      : lineDiff(previousVersion.promptBody, profile.promptBody);
  const diff = diffSummary(diffLines);
  const used = placeholdersIn(body);
  const unknown = used.filter((name) => !(KNOWN_PLACEHOLDERS as readonly string[]).includes(name));
  const sourceless = used.filter((name) => withoutSource.has(name));

  return (
    <Stack gap="md">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          label="Пошук"
          placeholder="назва профілю"
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
          }}
          data-testid="prompt-search"
          style={{ width: 220 }}
        />
        <Select
          label="Стан"
          placeholder="усі"
          clearable
          data={PROMPT_STATES.map((value) => ({ value, label: STATE_LABELS[value] ?? value }))}
          value={state}
          onChange={setState}
          data-testid="prompt-state-filter"
          style={{ width: 150 }}
        />
        <CreatePanel
          onReset={() => {
            create.reset();
            createForm.reset();
          }}
          zone="ai_generation"
          label="Додати промпт"
          testId="prompt"
          open={createOpen}
          onOpenChange={setCreateOpen}
          busy={create.isPending}
          error={create.error}
          onSubmit={createForm.onSubmit((values) => {
            create.mutate(values);
          })}
        >
          <TextInput
            size="xs"
            placeholder="назва"
            data-testid="create-prompt-name"
            style={{ width: 180 }}
            {...createForm.getInputProps('name')}
          />
          <TextInput
            size="xs"
            placeholder="модель"
            data-testid="create-prompt-model"
            style={{ width: 200 }}
            {...createForm.getInputProps('model')}
          />
          <Textarea
            size="xs"
            // Не `autosize`: він міряє елемент через ResizeObserver, якого немає в DOM
            // компонентного тесту, — а екран, що працює лише в браузері, це екран, чиї
            // регресії знаходить людина. Те саме рішення, що в `ParsePanel`.
            rows={3}
            placeholder="Огляд {brand} для {geo}…"
            data-testid="create-prompt-body"
            style={{ width: 380 }}
            {...createForm.getInputProps('promptBody')}
          />
        </CreatePanel>
      </Group>

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Промпт-профілів немає. Без профілю AI-блок шаблону не згенерує нічого."
        onRowClick={(row) => {
          setSelectedId(row.id);
          setBodyDraft(null);
          setTestContext({});
          testRun.reset();
        }}
        rowTestId={(row) => `prompt-row-${row.name}`}
        testId="prompt-table"
      />

      {selectedId === null ? null : detail.isPending ? (
        <Box className={classes.panel}>
          <Loader size="sm" />
        </Box>
      ) : profile === undefined ? null : (
        <Box className={classes.panel} data-testid="prompt-detail">
          <Group justify="space-between" mb="sm">
            <Group gap={8}>
              <Text className={classes.panelTitle}>{profile.name}</Text>
              <Badge size="sm" color={STATE_COLORS[profile.state] ?? 'slate'} data-testid="prompt-current-state">
                v{profile.version} · {STATE_LABELS[profile.state] ?? profile.state}
              </Badge>
            </Group>
            <Button
              size="compact-xs"
              variant="subtle"
              data-testid="prompt-close"
              onClick={() => {
                setSelectedId(null);
              }}
            >
              Закрити
            </Button>
          </Group>

          <Textarea
            rows={10}
            value={body}
            disabled={!canWrite}
            data-testid="prompt-body"
            onChange={(event) => {
              setBodyDraft(event.currentTarget.value);
            }}
          />

          {/*
           * Палітра — не декор: сервер відхиляє невідомий плейсхолдер, тож перелік має
           * збігатися з тим, проти чого йде валідація. Обидва боки читають один список із
           * `@seo/contracts` саме тому.
           */}
          <Group gap={4} mt={8} data-testid="prompt-palette">
            {KNOWN_PLACEHOLDERS.map((name: PromptPlaceholder) => (
              <Button
                key={name}
                size="compact-xs"
                variant={used.includes(name) ? 'light' : 'default'}
                color={withoutSource.has(name) ? 'amber' : 'slate'}
                disabled={!canWrite}
                data-testid={`prompt-var-${name}`}
                title={
                  withoutSource.has(name)
                    ? `${PLACEHOLDER_LABELS[name]} — джерела в базі немає, підставиться дужками`
                    : PLACEHOLDER_LABELS[name]
                }
                onClick={() => {
                  setBodyDraft(`${body}{${name}}`);
                }}
              >
                {`{${name}}`}
              </Button>
            ))}
          </Group>

          <Stack gap={2} mt={8}>
            {unknown.length === 0 ? null : (
              <Text className={classes.danger} data-testid="prompt-unknown">
                Невідомі плейсхолдери: {unknown.join(', ')} — сервер відхилить збереження
              </Text>
            )}
            {['brand', 'geo'].filter((name) => !used.includes(name)).length === 0 ? null : (
              <Text className={classes.danger} data-testid="prompt-missing-required">
                Немає обовʼязкових:{' '}
                {['brand', 'geo']
                  .filter((name) => !used.includes(name))
                  .map((name) => `{${name}}`)
                  .join(', ')}
              </Text>
            )}
            {/*
             * Окрема лінія, і навмисно не помилка: збереження пройде, генерація — теж, а в
             * текст доїде літерал у дужках. Єдине місце, де це видно завчасно.
             */}
            {sourceless.length === 0 ? null : (
              <Text className={classes.warn} data-testid="prompt-sourceless">
                Без джерела в базі: {sourceless.map((name) => `{${name}}`).join(', ')} — доїдуть у
                текст дужками
              </Text>
            )}
          </Stack>

          {canWrite ? (
            <Group gap="sm" mt="sm">
              <Button
                size="compact-xs"
                loading={revise.isPending}
                disabled={bodyDraft === null || bodyDraft === profile.promptBody}
                data-testid="prompt-save"
                onClick={() => {
                  revise.mutate({ id: profile.id, changes: { promptBody: body } });
                }}
              >
                Зберегти як нову версію
              </Button>
              <Button
                size="compact-xs"
                variant="default"
                loading={publish.isPending}
                disabled={profile.state === 'live'}
                data-testid="prompt-publish"
                onClick={() => {
                  publish.mutate({ id: profile.id });
                }}
              >
                Опублікувати
              </Button>
            </Group>
          ) : null}

          {/*
           * Тест-прогін іде тим самим шляхом, що й генерація: структурований вихід за
           * схемою. Прев'ю на вільному тексті перевіряло б інший код, ніж працює насправді.
           */}
          {canWrite && used.length > 0 ? (
            <Box mt="md" data-testid="prompt-test">
              <Text className={classes.dim} mb={6}>
                Тест-прогін: значення для плейсхолдерів цієї версії
              </Text>
              <Group gap={6} wrap="wrap">
                {(profile.placeholders.length > 0 ? profile.placeholders : used).map((name) => (
                  <TextInput
                    key={name}
                    size="xs"
                    placeholder={`{${name}}`}
                    value={testContext[name] ?? ''}
                    data-testid={`prompt-test-${name}`}
                    style={{ width: 150 }}
                    onChange={(event) => {
                      const { value } = event.currentTarget;
                      setTestContext((previous) => ({ ...previous, [name]: value }));
                    }}
                  />
                ))}
                <Button
                  size="compact-xs"
                  variant="default"
                  loading={testRun.isPending}
                  data-testid="prompt-test-run"
                  onClick={() => {
                    testRun.mutate({ id: profile.id, context: testContext });
                  }}
                >
                  Прогнати
                </Button>
              </Group>

              {testRun.data === undefined ? null : (
                <Stack gap={4} mt={8} data-testid="prompt-test-result">
                  {testRun.data.unresolved.length === 0 ? null : (
                    <Text className={classes.warn} data-testid="prompt-test-unresolved">
                      Не підставлено: {testRun.data.unresolved.map((n) => `{${n}}`).join(', ')}
                    </Text>
                  )}
                  <Text className={classes.dim} data-testid="prompt-test-words">
                    {testRun.data.words} слів ·{' '}
                    {testRun.data.withinWordRange ? 'у межах діапазону' : 'поза діапазоном'} ·{' '}
                    {testRun.data.model}
                  </Text>
                  <Text size="sm">{testRun.data.text}</Text>
                </Stack>
              )}
            </Box>
          ) : null}

          {/*
           * Історія версій — те, чим профіль відрізняється від поля з текстом. Блок шаблону
           * тримається за конкретний рядок, тож минулі версії лишаються відкривними.
           */}
          <Stack gap={2} mt="md" data-testid="prompt-versions">
            {profile.versions.map((version) => (
              <Group key={version.id} gap={8} data-testid={`prompt-version-${String(version.version)}`}>
                <Button
                  size="compact-xs"
                  variant={version.id === profile.id ? 'light' : 'subtle'}
                  onClick={() => {
                    setSelectedId(version.id);
                    setBodyDraft(null);
                  }}
                >
                  v{version.version}
                </Button>
                <Badge size="xs" color={STATE_COLORS[version.state] ?? 'slate'}>
                  {STATE_LABELS[version.state] ?? version.state}
                </Badge>
              </Group>
            ))}
          </Stack>

          {/*
           * Діфф із попередньою версією — те, заради чого профіль версіонується. Доки
           * `byId` віддавав лише номери версій, побачити, чим v3 відрізняється від v2, можна
           * було тільки відкривши кожну окремо й тримаючи різницю в голові.
           */}
          {previousVersion === undefined ? null : (
            <Box mt="md" data-testid="prompt-diff">
              <Text className={classes.dim} mb={6}>
                Проти v{previousVersion.version}: +{diff.added} / −{diff.removed} рядків
              </Text>
              <Stack gap={0}>
                {diffLines
                  .filter((line) => line.op !== 'same')
                  .map((line, index) => (
                    <Text
                      key={`${line.op}-${String(index)}`}
                      className={line.op === 'added' ? classes.diffAdded : classes.diffRemoved}
                      data-testid={`prompt-diff-${line.op}`}
                    >
                      {line.op === 'added' ? '+ ' : '− '}
                      {line.text}
                    </Text>
                  ))}
              </Stack>
            </Box>
          )}

          {revise.error ?? publish.error ?? testRun.error ? (
            <Text className={classes.danger} mt={8} data-testid="prompt-error" role="alert">
              {publicMessageOf(revise.error ?? publish.error ?? testRun.error)}
            </Text>
          ) : null}
        </Box>
      )}
    </Stack>
  );
}
